import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Surface } from "./Primitives";
import { CheckCircle, WarningCircle, XCircle, CaretDown, CaretRight } from "@phosphor-icons/react";
import { api } from "../lib/api";

const IMPORTANCE = {
  critical:  { label: "REQUIRED",  color: "text-red-400 border-red-500/40 bg-red-500/5" },
  important: { label: "IMPORTANT", color: "text-amber border-amber/40 bg-amber/5" },
  nice:      { label: "OPTIONAL",  color: "text-zinc-500 border-zinc-800 bg-zinc-900/40" },
};

export default function CompletenessCard({ aircraftId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/aircraft/${aircraftId}/completeness`);
      setData(r.data);
    } catch {/* ignore */}
  }, [aircraftId]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const pct = data.score_percent;
  const tone = pct === 100 ? "emerald" : pct >= 75 ? "amber" : pct >= 50 ? "orange" : "red";
  const toneClass = {
    emerald: "text-emerald-400 border-emerald-500/40",
    amber:   "text-amber border-amber/40",
    orange:  "text-orange-400 border-orange-500/40",
    red:     "text-red-400 border-red-500/40",
  }[tone];

  return (
    <Surface className="p-6" data-testid="completeness-card">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between text-left"
        data-testid="completeness-toggle"
      >
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className={`text-3xl font-semibold tabular-nums ${toneClass.split(" ")[0]}`}>{pct}%</div>
            <div>
              <div className="text-sm text-zinc-200 font-medium">Profile completeness</div>
              <div className="text-[11px] text-zinc-500 font-mono">
                {data.score_done}/{data.score_total} fields · {data.critical_done}/{data.critical_total} required
              </div>
            </div>
          </div>
          {data.missing_critical?.length > 0 && (
            <div className="mt-2 text-xs text-red-300 font-mono">
              {data.missing_critical.length} required item(s) missing — upload documents or fill in the profile to enable the status report.
            </div>
          )}
          {data.is_report_ready && (
            <div className="mt-2 text-xs text-emerald-300 font-mono">
              ✓ Status report ready to generate.
            </div>
          )}
        </div>
        <div className="text-zinc-500">
          {open ? <CaretDown size={20} /> : <CaretRight size={20} />}
        </div>
      </button>

      {open && (
        <div className="mt-5 pt-5 border-t border-zinc-800 space-y-4" data-testid="completeness-sections">
          {data.sections.map((s) => (
            <div key={s.title}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">{s.title}</div>
                <div className="text-[10px] text-zinc-500 font-mono">{s.done}/{s.total} ({s.percent}%)</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
                {s.items.map((it) => {
                  const Icon = it.ok ? CheckCircle : (it.importance === "critical" ? XCircle : WarningCircle);
                  const iconColor = it.ok
                    ? "text-emerald-400"
                    : it.importance === "critical" ? "text-red-400" : "text-amber";
                  return (
                    <div
                      key={`${s.title}-${it.key}`}
                      className="flex items-center gap-2.5 text-xs py-1"
                      data-testid={`completeness-item-${it.key}`}
                    >
                      <Icon size={14} className={iconColor} weight={it.ok ? "fill" : "regular"} />
                      <span className={`flex-1 ${it.ok ? "text-zinc-400" : "text-zinc-200"}`}>{it.label}</span>
                      {!it.ok && (
                        <span className={`text-[9px] font-mono px-1.5 py-0.5 border ${IMPORTANCE[it.importance].color}`}>
                          {IMPORTANCE[it.importance].label}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </Surface>
  );
}
